import { Transition } from "@headlessui/react";
import { ChevronRightIcon, PlusIcon } from "@xola/icons";
import { Alert, Button, FadeIn, flash } from "@xola/ui-kit";
import clsx from "clsx";
import { orderBy } from "lodash";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { MessageTemplate } from "types";
import { api } from "../../../api/api";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { InfiniteList } from "../../Common/InfiniteList";
import { Loader } from "../../Loader";
import { MessageTemplateForm } from "./MessageTemplateForm";
import { TemplateItem } from "./TemplateItem";

export const MessageTemplateList = () => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const urlParams = useParams<{ id?: string }>();
    const {
        data: templates,
        isLoading: isLoadingTemplates,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<MessageTemplate>("messageTemplates");

    const selectedTemplate = templates.find((template) => template.id === urlParams.id);
    const isExpanded = !!selectedTemplate;

    const handleTemplateItemClick = (template: MessageTemplate) => {
        if (selectedTemplate?.id === template.id && isExpanded) {
            history.push("/customers/messageTemplates");
            return;
        }

        history.push(`/customers/messageTemplates/${template.id}`);
    };

    const handleDeleteItem = async (template: MessageTemplate) => {
        try {
            await api.delete(`/messageTemplates/${template.id}`);
            invalidate("messageTemplates");
            flash.success("Successfully removed message template");
        } catch {
            flash.danger("Error on deleting message template");
        }
    };

    const handleExpandIconClick = () => {
        history.push(`/customers/messageTemplates`);
    };

    return (
        <div className={clsx("flex h-full lg:flex-row", !(isLoadingTemplates || isFetchingNextPage) && "pb-20")}>
            <FadeIn
                tag="span"
                className={clsx("flex", isExpanded ? "w-56 flex-shrink-0 flex-col lg:w-56 xl:w-60" : "w-[100%]")}
                enter="transition-all duration-250"
                enterFrom={clsx(isExpanded ? "w-full" : "opacity-0")}
                enterTo={clsx(isExpanded ? "w-56" : "opacity-100")}
            >
                {templates.length > 0 ? (
                    <>
                        <InfiniteList
                            hasMore={hasNextPage ?? false}
                            isLoading={isLoadingTemplates || isFetchingNextPage}
                            className={clsx("flex w-full flex-col", isExpanded && "border-r border-gray-light")}
                            onLoadMore={fetchNextPage}
                        >
                            {orderBy(templates, ["name"], ["asc"]).map((template: MessageTemplate) => (
                                <TemplateItem
                                    key={template.id}
                                    template={template}
                                    isActive={template.id === selectedTemplate?.id}
                                    isCollapsed={isExpanded}
                                    onClick={handleTemplateItemClick}
                                    onDelete={handleDeleteItem}
                                />
                            ))}
                        </InfiniteList>

                        {isExpanded && (
                            <div
                                data-testid="message-template-list-collapse-button"
                                className="absolute mt-5 ml-56 cursor-pointer rounded-full border border-gray-light bg-white px-2 py-1"
                                onClick={handleExpandIconClick}
                            >
                                <ChevronRightIcon size="small" />
                            </div>
                        )}
                    </>
                ) : (
                    <NoTemplatesMessage isLoading={isLoadingTemplates} />
                )}
            </FadeIn>

            {isExpanded && (
                <Transition
                    show={isExpanded}
                    appear={isExpanded}
                    as="div"
                    className="flex w-full flex-col overflow-y-auto"
                    enter="transition-all duration-250"
                    enterFrom="transform translate-x-full"
                    enterTo="transform translate-x-0"
                >
                    {selectedTemplate ? <MessageTemplateForm template={selectedTemplate} /> : null}
                </Transition>
            )}
        </div>
    );
};

const NoTemplatesMessage = ({ isLoading }: { isLoading: boolean }) => {
    const history = useHistory();

    const handleCreate = () => {
        history.push(`/customers/messageTemplates/create`);
    };

    return (
        <div className="m-4 mx-2 p-2 text-gray-darker" data-testid="message-template-list-no-templates-found">
            {isLoading ? (
                <Loader size="tiny" className="!p-1" text="Loading Templates" />
            ) : (
                <Alert color="primary">
                    <div className="flex p-2">
                        <div className="ml-2">
                            <div className="mb-3 flex space-x-2">
                                <span className="text-md font-bold">No Templates Found</span>
                            </div>
                            <span className="text-base">
                                You do not have any message templates yet. Please create your first template to get
                                started.
                            </span>
                            <div className="mt-2 flex justify-end">
                                <Button size="small" color="primary" icon={<PlusIcon />} onClick={handleCreate}>
                                    Create Template
                                </Button>
                            </div>
                        </div>
                    </div>
                </Alert>
            )}
        </div>
    );
};
